import styles from 'css/src/components/common/ui/Backdrop.module.scss';

import React from 'react';
import classNames from 'classnames';

import Spinner from 'js/src/components/common/ui/Spinner';

export const BACKDROP_Z_INDEX = {
    slide_overlay: 101,
    // Above $z-index-dashboard-header (1035) so a bulk action covers the app
    // header too, rather than leaving a live-looking strip across the top
    dashboard_backdrop: 1036,
    transcript_backdrop: 103,
    publishing: 1090,
    slide_loading: 2,
} as const;

type BaseBackdropProps = {
    show?: boolean;
    onClick?: VoidFunction;
    className?: string;
    zIndex?: number;
    positionFixed?: boolean;
    children?: React.ReactNode;
};

/**
 * A backdrop that takes up the entire height/width of its parent component.
 * - Its contents can be one of the exported variations below, or custom.
 * - This is a controlled component - its visibility is toggled by its parent.
 * - Pass `zIndex` (typically from `BACKDROP_Z_INDEX`) to place the backdrop above
 *   surrounding elements in the current stacking context.
 */
function BaseBackdrop({
    show = false,
    onClick,
    className = '',
    zIndex,
    positionFixed = false,
    children,
}: BaseBackdropProps) {
    const hasClickHandler = typeof onClick === 'function';
    const finalClassname = classNames(styles['common-ui-backdrop'], className, {
        [styles['clickable']]: hasClickHandler,
        [styles['fixed-backdrop']]: positionFixed,
    });
    const style = zIndex !== undefined ? { zIndex } : undefined;

    return show ? (
        <div className={finalClassname} style={style} onClick={onClick}>
            {children}
        </div>
    ) : null;
}
const Backdrop = React.memo(BaseBackdrop);

function BaseLoadingBackdrop(props: BaseBackdropProps) {
    return (
        <BaseBackdrop {...props}>
            <div className="content-loading">
                <Spinner className="text-white" />
            </div>
        </BaseBackdrop>
    );
}
const LoadingBackdrop = React.memo(BaseLoadingBackdrop);

type BaseBlurBackdropProps = {
    children?: React.ReactNode;
} & BaseBackdropProps;

function BaseBlurBackdrop(props: BaseBlurBackdropProps) {
    const propClone = { ...props };
    delete propClone.children;

    return (
        <BaseBackdrop {...propClone}>
            {
                // only render the blur if the backdrop is visible for performance reasons
                props.show && <div className="content-blur" />
            }
            {props.children}
        </BaseBackdrop>
    );
}

const BlurBackdrop: React.FC<BaseBlurBackdropProps> = React.memo(BaseBlurBackdrop);

export { Backdrop, BlurBackdrop, LoadingBackdrop };
